import React, { FC, Fragment } from 'react';
import { Link, Typography } from '@mui/material';

/**
 * The assistant's marks in otherwise plain text: an article link, [Title](article:123456),
 * a page link, [Name](page:/lab-settings/invoicing), and a UI name in asterisks,
 * *Nästa steg*, shown in italics. A stray **double** pair is treated like a single one.
 */
const MARKUP = /\[([^\]\n]+)\]\(article:(\d+)\)|\[([^\]\n]+)\]\(page:(\/[^)\s]+)\)|\*{1,2}([^*\n]{1,80}?)\*{1,2}/g;

type TextPart = { text: string; articleId?: string; pagePath?: string; isUiName?: boolean };

const splitMarkup = (text: string): TextPart[] => {
    const parts: TextPart[] = [];
    let lastIndex = 0;
    const pattern = new RegExp(MARKUP.source, 'g');
    let match = pattern.exec(text);
    while (match) {
        parts.push({ text: text.slice(lastIndex, match.index) });
        if (match[2]) {
            parts.push({ text: match[1], articleId: match[2] });
        } else if (match[4]) {
            parts.push({ text: match[3], pagePath: match[4] });
        } else {
            parts.push({ text: match[5], isUiName: true });
        }
        lastIndex = match.index + match[0].length;
        match = pattern.exec(text);
    }
    parts.push({ text: text.slice(lastIndex) });
    return parts.filter((part) => part.text);
};

type AssistantMessageTextProps = {
    text: string;
    onArticleClicked: (articleId: string) => void;
    /** Opens a page in Denthub (in-app navigation only). */
    onNavigate: (path: string) => void;
    /** Points at a UI element on the current page by its visible text. */
    onShowUiElement: (name: string) => void;
};

/** Message text with article links that open the article, and UI names in italics. */
const AssistantMessageText: FC<AssistantMessageTextProps> = ({
    text,
    onArticleClicked,
    onNavigate,
    onShowUiElement,
}: AssistantMessageTextProps) => {
    const parts = splitMarkup(text);
    return (
        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
            {parts.map((part, index) => {
                if (part.articleId) {
                    return (
                        <Link
                            key={index}
                            component="button"
                            variant="body2"
                            sx={{ verticalAlign: 'baseline', textAlign: 'left' }}
                            onClick={() => onArticleClicked(part.articleId as string)}
                        >
                            {part.text}
                        </Link>
                    );
                }
                if (part.pagePath) {
                    const pagePath = part.pagePath;
                    return (
                        <Link
                            key={index}
                            component="button"
                            variant="body2"
                            sx={{ verticalAlign: 'baseline', textAlign: 'left', fontWeight: 500 }}
                            onClick={() => onNavigate(pagePath)}
                        >
                            {part.text}
                        </Link>
                    );
                }
                if (part.isUiName) {
                    const name = part.text;
                    return (
                        <Link
                            key={index}
                            component="button"
                            variant="body2"
                            color="inherit"
                            underline="always"
                            title={name}
                            sx={{
                                verticalAlign: 'baseline',
                                fontStyle: 'italic',
                                // Underlined in the text colour, so it reads as clickable without looking like a link.
                                textDecorationColor: 'currentColor',
                                textUnderlineOffset: '2px',
                            }}
                            onClick={() => onShowUiElement(name)}
                        >
                            {name}
                        </Link>
                    );
                }
                return <Fragment key={index}>{part.text}</Fragment>;
            })}
        </Typography>
    );
};

export default AssistantMessageText;
